import styled from 'styled-components';
import BucketIcon from '@svg/bucket_icon';
import toolClient from '@requests/telnyx/tools.requests';

import { v4 as uuidv4 } from 'uuid';
import { AssistantTool } from './AssistantTool';
import { Label } from '@common/Label';
import { Alert, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Stack } from '@mui/material';
import { Button } from '@common/Button';
import { useState } from 'react';
import { useTheme } from '../../../../../../styles';
import { useParams } from 'react-router-dom';
import { Drawer } from '@components/Reporting/NodeExecutionSidebar';
import { AssistantContainer, AssistantContainerSection } from '../../Assistants';
import { AssistantToolDrawer } from './AssistantToolDrawer/AssistantToolDrawer';
import { AITool, ToolDrawerOpen, ToolExecution, ToolModification, ToolType } from './tool.types';
import { RequestError } from '@requests/common';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@common/hooks/useLoader';
import { AIAssistant } from '@requests/telnyx';
import { showTelnyxFeatures, State } from '@store/index';
import { useSelector } from 'react-redux';
import { getToolDefault } from './tool.defaults';
import { DeleteAssitantTool } from '@components/Modals/Assistants/DeleteAssistantTool';

import {
  PublicOutlined,
  DescriptionOutlined,
  KeyboardArrowDownOutlined,
  PhoneDisabledOutlined,
  CodeOutlined,
  AttachMoneyOutlined,
  ContentCopyOutlined,
} from '@mui/icons-material';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  padding: 0.5rem;
  gap: 4px;
  border: 1px solid ${({ theme }) => theme.line.light};
`;

export const ToolSnackbarOrigin = { horizontal: 'center' as const, vertical: 'bottom' as const };

interface Props {
  assistant: AIAssistant;
  isAssistantsLoading: boolean;
}

export const AssistantTools: React.FC<Props> = ({ assistant }) => {
  const theme = useTheme();

  const { assistantId } = useParams();

  const [openTool, setOpenTool] = useState<ToolDrawerOpen | null>(null);
  const [voiceTool, setVoiceTool] = useState<null | HTMLElement>(null);
  const [flowTool, setFlowTool] = useState<null | HTMLElement>(null);
  const [changesMade, setChangesMade] = useState(false);

  const isTelnyxUser = useSelector((state: State) => showTelnyxFeatures(state));
  const hasBucketRetrieval = assistant.tools.some((tool) => tool.execution === ToolExecution.BUCKET_RETRIEVAL);
  const hasHangup = assistant.tools.some((tool) => tool.execution === ToolExecution.HANGUP_CALL);

  const { data: flowTools = [], isLoading: flowToolsLoading } = useQuery<
    AITool[],
    RequestError,
    AITool[],
    [string, string | undefined]
  >({
    queryKey: ['ai_assistants_tools', assistantId],
    queryFn: toolClient.list,
    retry: false,
  });

  const createTool = (type: ToolType, execution: ToolExecution) => {
    // get the default configuration for the tool
    const { metadata } = getToolDefault(execution);

    const tool: AITool = {
      id: uuidv4(),
      assistant_id: assistantId as string,
      name: execution === ToolExecution.HANGUP_CALL ? 'hangup' : '',
      description: '',
      instructions: '',
      type,
      execution,
      metadata,
      arguments: [],
      settings: {
        tool_choice: 'auto',
        events: [],
      },
    };

    setVoiceTool(null);
    setFlowTool(null);
    setOpenTool({ tool, modification: ToolModification.CREATE });
  };

  const openCopyToolDrawer = () => {
    const tool: AITool = {
      id: uuidv4(),
      assistant_id: assistantId as string,
      name: '',
      description: '',
      instructions: '',
      type: ToolType.FLOW,
      execution: '' as ToolExecution,
      metadata: undefined,
      arguments: [],
      settings: {
        tool_choice: 'auto',
        events: [],
      },
    };

    setFlowTool(null);
    setOpenTool({ tool, modification: ToolModification.CREATE, isCopyMode: true });
  };

  const noFlowTools = flowTools.length === 0 && !flowToolsLoading;
  const noVoiceTools = assistant.tools.length === 0;

  return (
    <AssistantContainer>
      <AssistantContainerSection>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Button small endIcon={<KeyboardArrowDownOutlined />} onClick={(e) => setFlowTool(e.currentTarget)}>
            Add Flow Tool
          </Button>
          <Button
            small
            endIcon={<KeyboardArrowDownOutlined />}
            onClick={(e) => setVoiceTool(e.currentTarget)}
            disabled={hasBucketRetrieval && hasHangup}
          >
            Add Voice Tool
          </Button>
        </Stack>
      </AssistantContainerSection>
      <Divider />
      <AssistantContainerSection>
        <Menu
          id='add-flow-tool'
          anchorEl={flowTool}
          open={Boolean(flowTool)}
          onClose={() => setFlowTool(null)}
          sx={{ minWidth: '300px' }}
          transformOrigin={{ vertical: -10, horizontal: 0 }}
          slotProps={{
            paper: { sx: { minWidth: '400px', padding: '0.5rem 0', border: `1px solid ${theme.line.light}` } },
          }}
          MenuListProps={{ 'aria-labelledby': 'add-voice-tool' }}
        >
          <MenuItem onClick={() => createTool(ToolType.FLOW, ToolExecution.BUCKET_SEARCH)}>
            <ListItemIcon>
              <BucketIcon height={20} />
            </ListItemIcon>
            <ListItemText primary='Bucket Search' secondary='Search bucket(s) for context.' />
          </MenuItem>
          <MenuItem onClick={() => createTool(ToolType.FLOW, ToolExecution.STATIC_DOCUMENT)}>
            <ListItemIcon>
              <DescriptionOutlined sx={{ fontSize: '20px' }} />
            </ListItemIcon>
            <ListItemText primary='Static Document' secondary='Output static text content.' />
          </MenuItem>
          <MenuItem onClick={() => createTool(ToolType.FLOW, ToolExecution.HTTP_REQUEST)}>
            <ListItemIcon>
              <PublicOutlined sx={{ fontSize: '20px' }} />
            </ListItemIcon>
            <ListItemText primary='HTTP Request' secondary='Send a HTTP request.' />
          </MenuItem>
          {isTelnyxUser && (
            <MenuItem onClick={() => createTool(ToolType.FLOW, ToolExecution.TELNYX_API_SEARCH)}>
              <ListItemIcon>
                <CodeOutlined sx={{ fontSize: '20px' }} />
              </ListItemIcon>
              <ListItemText primary='Telnyx API Search' secondary='Search the Telnyx API docs.' />
            </MenuItem>
          )}
          {isTelnyxUser && (
            <MenuItem onClick={() => createTool(ToolType.FLOW, ToolExecution.TELNYX_PRICING_CALCULATOR)}>
              <ListItemIcon>
                <AttachMoneyOutlined sx={{ fontSize: '20px' }} />
              </ListItemIcon>
              <ListItemText primary='Telnyx Pricing' secondary='Search Telnyx product pricing models.' />
            </MenuItem>
          )}
          <Divider />
          <MenuItem onClick={openCopyToolDrawer}>
            <ListItemIcon>
              <ContentCopyOutlined sx={{ fontSize: '16px' }} />
            </ListItemIcon>
            <ListItemText primary='Copy Existing Tool' secondary='Copy configuration from an existing tool.' />
          </MenuItem>
        </Menu>
        <Menu
          id='add-voice-tool'
          anchorEl={voiceTool}
          open={Boolean(voiceTool)}
          onClose={() => setVoiceTool(null)}
          sx={{ minWidth: '300px' }}
          transformOrigin={{ vertical: -10, horizontal: 0 }}
          slotProps={{
            paper: { sx: { minWidth: '400px', padding: '0.5rem 0', border: `1px solid ${theme.line.light}` } },
          }}
          MenuListProps={{ 'aria-labelledby': 'add-voice-tool' }}
        >
          <MenuItem
            disabled={hasBucketRetrieval}
            onClick={() => createTool(ToolType.VOICE, ToolExecution.BUCKET_RETRIEVAL)}
          >
            <ListItemIcon>
              <BucketIcon height={20} />
            </ListItemIcon>
            <ListItemText primary='Bucket Retrieval' secondary='Search bucket(s) for context.' />
          </MenuItem>
          <MenuItem disabled={hasHangup} onClick={() => createTool(ToolType.VOICE, ToolExecution.HANGUP_CALL)}>
            <ListItemIcon>
              <PhoneDisabledOutlined sx={{ fontSize: '20px' }} />
            </ListItemIcon>
            <ListItemText primary='Hangup Call' secondary='Terminate the active phone call.' />
          </MenuItem>
        </Menu>
        <Stack spacing={1}>
          <Label
            title='Flow Tools'
            tooltip='Flow Tools can be used by the AI Assistant nodes in any workflow. They are not compatible with the external AI Assistant intergrations, such as the TeXML Assistant or starting the AI Assistant on a call control call. '
            // onClick={noFlowTools ? undefined : (e) => setFlowTool(e.currentTarget)}
          />
          <Container>
            {noFlowTools && <Alert severity='info'>This assistant does not have any flow tools assigned.</Alert>}
            {flowToolsLoading && (
              <Stack
                sx={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '0.25rem 0',
                }}
              >
                <Loader />
              </Stack>
            )}
            {flowTools.map((tool) => (
              <AssistantTool key={tool.id} tool={tool} setOpenTool={setOpenTool} />
            ))}
          </Container>
        </Stack>
        <Stack spacing={1}>
          <Label
            title='Voice Tools'
            tooltip='Voice Tools can be used by the Telnyx AI Assistant voice integrations, such as the TeXML Assistant and starting the AI Assistant on a call control call. They are not compatible with AI Assistant nodes in workflows.'
          />
          <Container>
            {noVoiceTools && <Alert severity='info'>This assistant does not have any voice tools assigned.</Alert>}
            {assistant.tools.map((tool) => (
              <AssistantTool key={tool.id} tool={tool} setOpenTool={setOpenTool} />
            ))}
          </Container>
        </Stack>
        <Drawer open={Boolean(openTool)} anchor='right' onClose={() => (!changesMade ? setOpenTool(null) : null)}>
          {openTool && (
            <AssistantToolDrawer openTool={openTool} setOpenTool={setOpenTool} setChangesMade={setChangesMade} />
          )}
        </Drawer>
        <DeleteAssitantTool />
      </AssistantContainerSection>
    </AssistantContainer>
  );
};
